import { analytics } from '@/features/analytics/events'
import { Spinner } from '@/ui-kit'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useCallback, useState } from 'react'
import { useAccessGatePhrase, useAccessGateStatus, useSubmitAccessGateCheck } from '../../api'
import type { NectarProjectType } from '../../api/models'
import { type MicCheckMetric, MicCheckPanel, type MicCheckPanelProps } from './MicCheckPanel'
import type { MicCheckResult } from './micCheckAnalysis'

export const RECORDING_PROJECT_TYPES: ReadonlySet<NectarProjectType> = new Set<NectarProjectType>([
  'AUDIO_RECORDING',
  'FREE_SPEECH',
  'VOICE_CONVERSATION',
  'PROFICIENCY_ASSESSMENT'
])

const VALID_METRICS: readonly MicCheckMetric[] = ['noise', 'snr', 'voice', 'clip']

export type MicCheckGateConfig = Omit<MicCheckPanelProps, 'onPass' | 'onFail' | 'onDismiss'> & {
  disabled?: boolean
}

export type MicCheckGateProps = {
  projectId: string
  enrollmentId: string
  screeningLanguage: string
  accessGateKind: 'LANGUAGE' | 'CONSENT'
  /** Fixed phrase; when null a random pool phrase is fetched. */
  accessGatePhrase: string | null
  children: ReactNode
  config?: MicCheckGateConfig
}

export const parseMicCheckConfig = (typeConfig: Record<string, unknown> | null | undefined): MicCheckGateConfig => {
  const raw =
    typeConfig?.micCheck && typeof typeConfig.micCheck === 'object'
      ? (typeConfig.micCheck as Record<string, unknown>)
      : null

  const visibleMetrics =
    raw && Array.isArray(raw.visibleMetrics)
      ? raw.visibleMetrics.filter((v): v is MicCheckMetric => VALID_METRICS.includes(v as MicCheckMetric))
      : []

  return raw
    ? {
        ...(typeof raw.disabled === 'boolean' && { disabled: raw.disabled }),
        ...(typeof raw.prompt === 'string' && { prompt: raw.prompt }),
        ...(typeof raw.durationSec === 'number' && raw.durationSec > 0 && { durationSec: raw.durationSec }),
        ...(typeof raw.showDevicePicker === 'boolean' && { showDevicePicker: raw.showDevicePicker }),
        ...(typeof raw.showDetails === 'boolean' && { showDetails: raw.showDetails }),
        ...(typeof raw.blockOnFail === 'boolean' && { blockOnFail: raw.blockOnFail }),
        ...(visibleMetrics.length > 0 && { visibleMetrics })
      }
    : {}
}

export const MicCheckGate = ({
  projectId,
  enrollmentId,
  screeningLanguage,
  accessGateKind,
  accessGatePhrase,
  children,
  config
}: MicCheckGateProps) => {
  const { disabled, ...panelConfig } = config ?? {}
  const queryClient = useQueryClient()
  const [justSubmitted, setJustSubmitted] = useState(false)

  // Server-driven so it can't be bypassed via localStorage.
  const statusQuery = useAccessGateStatus(enrollmentId, { enabled: !disabled })
  const statusResolved = disabled || statusQuery.isSuccess || statusQuery.isError
  const isPassed = disabled || justSubmitted || statusQuery.data?.submitted === true

  const phraseQuery = useAccessGatePhrase(screeningLanguage, {
    enabled: statusResolved && !isPassed && !accessGatePhrase
  })
  const promptText = accessGatePhrase ?? phraseQuery.data?.text
  const phraseId = accessGatePhrase ? null : (phraseQuery.data?.id ?? null)
  const { mutate: submitCheck } = useSubmitAccessGateCheck()

  const proceed = useCallback(() => {
    setJustSubmitted(true)
    queryClient.invalidateQueries({ queryKey: ['NECTAR_ACCESS_GATE_STATUS', enrollmentId] })
  }, [queryClient, enrollmentId])

  const uploadScreening = useCallback(
    (blob: Blob | null) => {
      if (!blob) return
      submitCheck({ enrollmentId, phraseId, blob })
      analytics.languageScreeningSubmitted(projectId, screeningLanguage)
    },
    [submitCheck, enrollmentId, phraseId, screeningLanguage, projectId]
  )

  const handlePass = useCallback(
    (_result: MicCheckResult, blob: Blob | null) => {
      uploadScreening(blob)
      proceed()
      analytics.micCheckPassed(projectId)
    },
    [uploadScreening, proceed, projectId]
  )

  const handleDismiss = useCallback(
    (result: MicCheckResult, blob: Blob | null) => {
      uploadScreening(blob)
      proceed()
      analytics.micCheckDismissed(projectId, result.issue)
    },
    [uploadScreening, proceed, projectId]
  )

  if (!statusResolved) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  return isPassed ? (
    <>{children}</>
  ) : (
    <MicCheckPanel
      {...panelConfig}
      prompt={promptText ?? panelConfig.prompt}
      kind={accessGateKind}
      gradeQuality={accessGateKind !== 'CONSENT'}
      onPass={handlePass}
      onDismiss={handleDismiss}
    />
  )
}
